import type { AddressDTO, CollectionImportRowDTO } from '@/api/trafikportalen';
import type { MappedColumns } from '@/components/Import/ImportColumnMapper';
import { normalizeCareOf } from '@/helpers/careOf';
import { convertNinTo12Digit } from '@/helpers/formatting';
import { FormattedStudent } from '@/types/students';

type NestedObject = Record<string, unknown>;

export const trimImportCellValue = (cell: unknown): string => {
  if (cell === null || cell === undefined) return '';
  if (typeof cell === 'string') return cell.trim();
  return String(cell).trim();
};

const formatZipCode = (value: string): string => {
  const digits = value.replace(/\s/g, '');
  if (/^\d{5}$/.test(digits)) {
    return `${digits.slice(0, 3)} ${digits.slice(3)}`;
  }
  return value;
};

export const formatImportNin = (value: string): string => {
  const trimmed = value.trim();
  if (!/^\d{6}(\d{2})?-?[A-Z\d]{2}\d{2}$/.test(trimmed)) return trimmed;
  const full = convertNinTo12Digit(trimmed);
  return `${full.slice(0, 8)}-${full.slice(8)}`;
};

export const transformToApiStructure = <T extends NestedObject>(
  flatData: Record<string, string>,
): T => {
  const result: NestedObject = {};

  for (const [apiField, value] of Object.entries(flatData)) {
    // Skip undefined/null values, but allow empty strings
    if (value === undefined || value === null) continue;

    const normalized = trimImportCellValue(value);

    if (apiField.includes('.')) {
      const [parent, child] = apiField.split('.');
      if (!result[parent]) {
        result[parent] = {};
      }
      (result[parent] as Record<string, string>)[child] = normalized;
    } else {
      result[apiField] = normalized;
    }
  }

  return result as T;
};

const ADDRESS_FIELDS = [
  'delivery_address',
  'guardian1_address',
  'guardian2_address',
  'school_address',
] as const;

/**
 * Strips incomplete address objects (where street, zip_code, or city is empty)
 * before sending rows to the backend. The backend's request schema enforces
 * `minLength: 1` on each address sub-field and rejects the entire batch
 * otherwise. The original (partial) address values stay in `importData` so the
 * user can see and edit them in the row detail dialog.
 */
export const sanitizeImportRowsForValidation = (
  rows: CollectionImportRowDTO[],
): CollectionImportRowDTO[] => {
  return rows.map((row) => {
    let next: CollectionImportRowDTO | undefined;
    for (const field of ADDRESS_FIELDS) {
      const addr = row[field];
      if (addr && !isValidAddress(addr)) {
        next = next ?? { ...row };
        delete (next as unknown as Record<string, unknown>)[field];
      }
    }
    return next ?? row;
  });
};

export const isValidAddress = (address: unknown): address is AddressDTO => {
  if (!address || typeof address !== 'object') return false;
  const addr = address as Record<string, unknown>;

  const hasStreet = typeof addr.street === 'string' && addr.street.length > 0;
  const hasZipCode =
    (typeof addr.zip_code === 'string' && addr.zip_code.length > 0) ||
    (typeof addr.zip_code === 'number' && String(addr.zip_code).length > 0);
  const hasCity = typeof addr.city === 'string' && addr.city.length > 0;

  return hasStreet && hasZipCode && hasCity;
};

interface TransformToCollectionImportRowsOptions {
  disableHalfProduct?: boolean;
}

export const transformToCollectionImportRows = (
  headers: string[],
  rows: string[][],
  mappedColumns: MappedColumns,
  existingStudents: FormattedStudent[],
  options?: TransformToCollectionImportRowsOptions,
): CollectionImportRowDTO[] => {
  const disableHalfProduct = options?.disableHalfProduct ?? false;
  const filteredColumns = Object.fromEntries(
    Object.entries(mappedColumns).filter(
      ([, apiField]) => !apiField.includes('do_not_use'),
    ),
  );

  return rows.map((row, index) => {
    const flatData: Record<string, string> = {};

    for (const [columnHeader, apiField] of Object.entries(filteredColumns)) {
      const colIndex = headers.indexOf(columnHeader);
      if (colIndex === -1) {
        console.warn(
          `Row ${index + 1}: Column "${columnHeader}" not found in headers`,
        );
        continue;
      }
      flatData[apiField] = trimImportCellValue(row[colIndex]);
    }

    const transformed = transformToApiStructure<NestedObject>(flatData);

    const rawNin = transformed['national_identity_number'];
    const formattedNinForMatch =
      rawNin !== undefined && rawNin !== null && String(rawNin).length > 0
        ? formatImportNin(trimImportCellValue(String(rawNin)))
        : '';

    const existing = formattedNinForMatch
      ? existingStudents.find(
          (s) => s.national_identity_number === formattedNinForMatch,
        )
      : undefined;

    const result: CollectionImportRowDTO = {
      row_number: index + 1,
      half_product: false,
    };

    if (existing?.phone) {
      result.phone = existing.phone;
    }

    // Simple string fields
    const stringFields = [
      'first_name',
      'last_name',
      'email',
      'national_identity_number',
      'guardian1_email',
      'guardian2_email',
      'school_name',
      'school_bus_stop',
      'guardian1_bus_stop',
      'guardian2_bus_stop',
    ] as const;

    for (const field of stringFields) {
      if (transformed[field]) {
        let value = trimImportCellValue(transformed[field]);
        if (field === 'national_identity_number') {
          value = formatImportNin(value);
        }
        result[field] = value;
      }
    }

    if (!disableHalfProduct && transformed.half_product !== undefined) {
      const v = String(transformed.half_product).toLowerCase();
      result.half_product = v === 'true' || v === '1' || v === 'ja';
    }

    // Address fields
    const addressFields = [
      'delivery_address',
      'guardian1_address',
      'guardian2_address',
      'school_address',
    ] as const;

    for (const field of addressFields) {
      const raw = transformed[field];
      if (!raw || typeof raw !== 'object') continue;

      const addr = raw as Partial<AddressDTO>;
      const street = trimImportCellValue(addr.street ?? '');
      const zipCode = trimImportCellValue(addr.zip_code ?? '');
      const city = trimImportCellValue(addr.city ?? '');
      const careOf = normalizeCareOf(addr.care_of);

      // Keep partial addresses so the user can see/edit them in the row
      // detail dialog. The backend will validate completeness.
      if (street || zipCode || city || careOf) {
        result[field] = {
          street,
          zip_code: zipCode ? formatZipCode(zipCode) : '',
          city,
          ...(careOf && { care_of: careOf }),
        };
      }
    }

    return result;
  });
};
